import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmationDialog, useDialog } from '@gokrew/ui';
import {
  Mail,
  Link2,
  RefreshCw,
  Unplug,
  ShieldCheck,
  ArrowUpRight,
  Check,
  AlertCircle,
  CalendarDays,
} from 'lucide-react';
import {
  connectCrmInbox,
  disconnectCrmInbox,
  getCrmInboxes,
  syncCrmInbox,
} from '@/api/crm';
import type { CrmInboxProvider } from '@/api/crm';
import { useCrmFormat } from './crm-format';
import { CrmButton, CrmError } from './CrmShared';
import styles from './Crm.module.css';
export const CrmInbox = () => {
  const { t, date } = useCrmFormat();
  const client = useQueryClient();
  const { openDialog } = useDialog();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const inboxes = useQuery({
    queryKey: ['crm', 'inboxes'],
    queryFn: getCrmInboxes,
  });
  const connect = async (provider: CrmInboxProvider) => {
    setBusy(provider);
    setError('');
    setResult('');
    try {
      const { url } = await connectCrmInbox(provider);
      const target = new URL(url);
      if (target.protocol !== 'https:')
        throw new Error('Invalid authorization URL');
      window.location.assign(target.href);
    } catch {
      setError(t('crm.workspace.inbox_connect_error'));
      setBusy(null);
    }
  };
  const sync = async (provider: CrmInboxProvider) => {
    setBusy(provider);
    setError('');
    setResult('');
    try {
      const value = await syncCrmInbox(provider);
      setResult(
        t('crm.workspace.sync_result', { count: value.imported }) +
          (value.truncated ? ` ${t('crm.workspace.sync_more')}` : '')
      );
      await client.invalidateQueries({ queryKey: ['crm'] });
    } catch {
      setError(t('crm.workspace.inbox_sync_error'));
    } finally {
      setBusy(null);
    }
  };
  const disconnect = (provider: CrmInboxProvider) =>
    openDialog({
      title: t('crm.workspace.disconnect_inbox'),
      content: (
        <ConfirmationDialog
          waitForSubmit
          variant="destructive"
          onSubmit={async () => {
            await disconnectCrmInbox(provider);
            await client.invalidateQueries({ queryKey: ['crm', 'inboxes'] });
          }}
        >
          <p>{t('crm.workspace.disconnect_description')}</p>
        </ConfirmationDialog>
      ),
    });
  if (inboxes.isPending)
    return (
      <p role="status" className={styles.loading}>
        {t('crm.loading')}
      </p>
    );
  if (inboxes.isError) return <CrmError retry={() => void inboxes.refetch()} />;
  return (
    <div className={styles.inboxPage}>
      <div className={styles.inboxIntro}>
        <span className={styles.inboxIcon}>
          <Mail />
        </span>
        <div>
          <h2>{t('crm.workspace.connect_inbox')}</h2>
          <p>{t('crm.workspace.inbox_description')}</p>
        </div>
      </div>
      {error && (
        <div role="alert" className={styles.errorBox}>
          {error}
        </div>
      )}
      {result && (
        <div role="status" className={styles.successBox}>
          <Check />
          {result}
        </div>
      )}
      <div className={styles.inboxProviders}>
        {inboxes.data.map((inbox) => (
          <section key={inbox.provider} className={styles.panel}>
            <div className={styles.providerHeading}>
              <span className={styles.providerMark}>
                {inbox.provider === 'google' ? (
                  'G'
                ) : (
                  <span className={styles.microsoftMark}>
                    <i />
                    <i />
                    <i />
                    <i />
                  </span>
                )}
              </span>
              <div>
                <h3>{t(`crm.workspace.providers.${inbox.provider}`)}</h3>
                <p>{inbox.email || t('crm.workspace.personal_connection')}</p>
              </div>
              <span
                className={`${styles.statusChip} ${inbox.status === 'connected' ? styles.connected : ''}`}
              >
                {inbox.status === 'connected' ? (
                  <Check />
                ) : inbox.status === 'needs_reconnect' ? (
                  <AlertCircle />
                ) : (
                  <Link2 />
                )}
                {t(`crm.workspace.inboxStatus.${inbox.status}`)}
              </span>
            </div>
            <p className={styles.providerDescription}>
              {t(`crm.workspace.provider_descriptions.${inbox.provider}`)}
            </p>
            {!inbox.configured ? (
              <div className={styles.setupRequired}>
                <AlertCircle />
                <div>
                  <strong>{t('crm.workspace.inbox_setup_required')}</strong>
                  <p>{t('crm.workspace.inbox_setup_description')}</p>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.syncInfo}>
                  <span>{t('crm.workspace.last_sync')}</span>
                  <strong>
                    {inbox.lastSyncedAt
                      ? date(inbox.lastSyncedAt, true)
                      : t('crm.workspace.never_synced')}
                  </strong>
                </div>
                {inbox.status === 'connected' && (
                  <div className={styles.syncInfo}>
                    <span>{t('crm.workspace.calendar')}</span>
                    <strong>
                      {!inbox.calendar
                        ? t('crm.workspace.calendar_off')
                        : inbox.calendarSyncedAt
                          ? date(inbox.calendarSyncedAt, true)
                          : t('crm.workspace.never_synced')}
                    </strong>
                  </div>
                )}
                {inbox.status === 'connected' && !inbox.calendar && (
                  <p className={styles.muted}>
                    {t('crm.workspace.calendar_reconnect')}
                  </p>
                )}
                {inbox.status === 'needs_reconnect' && (
                  <p className={styles.error}>
                    {t('crm.workspace.inbox_reconnect_description')}
                  </p>
                )}
                <div className={styles.providerActions}>
                  {inbox.status === 'connected' ? (
                    <>
                      <CrmButton
                        variant="adminPrimary"
                        IconPrefix={RefreshCw}
                        disabled={!!busy}
                        onClick={() => sync(inbox.provider)}
                      >
                        {t(
                          busy === inbox.provider
                            ? 'crm.workspace.syncing'
                            : 'crm.workspace.sync_now'
                        )}
                      </CrmButton>
                      {!inbox.calendar && (
                        <CrmButton
                          variant="outline"
                          IconPrefix={CalendarDays}
                          disabled={!!busy}
                          onClick={() => connect(inbox.provider)}
                        >
                          {t('crm.workspace.add_calendar')}
                        </CrmButton>
                      )}
                      <CrmButton
                        variant="outline"
                        IconPrefix={Unplug}
                        disabled={!!busy}
                        onClick={() => disconnect(inbox.provider)}
                      >
                        {t('crm.workspace.disconnect')}
                      </CrmButton>
                    </>
                  ) : (
                    <CrmButton
                      variant="adminPrimary"
                      IconSuffix={ArrowUpRight}
                      disabled={!!busy}
                      onClick={() => connect(inbox.provider)}
                    >
                      {t(
                        busy === inbox.provider
                          ? 'crm.workspace.connecting'
                          : 'crm.workspace.connect'
                      )}
                    </CrmButton>
                  )}
                </div>
              </>
            )}
          </section>
        ))}
      </div>
      <section className={`${styles.panel} ${styles.inboxExplainer}`}>
        <ShieldCheck />
        <div>
          <h3>{t('crm.workspace.inbox_how_title')}</h3>
          <p>{t('crm.workspace.inbox_how_description')}</p>
        </div>
      </section>
    </div>
  );
};
